// ── Strata-Manager Email-Domain Registry (Phase 1.6, v5.14.22) ──────────
// Per Peter 2026-05-03: the contact's email domain is the canonical Requester
// identity. RFQ documents talk about the building ("Owners Corporation,
// Strata Plan 1645"), but the entity actually requesting + paying is the
// strata management company that sent the email. Mapping a known domain to
// its registered Australian company name lets QuoteMaker correctly identify
// the Requester at intake time, AND drives the Phase 1.6 re-organize sweep
// that fixes existing mislabeled records.
//
// Reference data — append rows as new strata managers land. Lower-case keys.
// abnHint is optional metadata (the actual ABN can be filled in later via
// the CRM detail panel; this is just a scratch placeholder).
export const STRATA_MANAGER_DOMAINS = {
    'nswstrata.com.au':      { canonicalName: 'NSW Strata',                  abnHint: null },
    'picagroup.com.au':       { canonicalName: 'PICA Group',                 abnHint: null },
    'bright-duggan.com.au':   { canonicalName: 'Bright & Duggan Pty Ltd',    abnHint: null },
    'stratapartners.com.au':  { canonicalName: 'Strata Partners',            abnHint: null },
    'bcssm.com.au':           { canonicalName: 'BCS Strata Management',      abnHint: null },
    // Common large-portfolio Australian strata managers — pre-populated so
    // first-touch projects don't get mislabeled even before they're seen
    // in the CRM register. Update names as you confirm them.
    'strata.com.au':                  { canonicalName: 'Strata Choice',                 abnHint: null },
    'netstrata.com.au':               { canonicalName: 'Netstrata',                     abnHint: null },
    'aplstrata.com.au':               { canonicalName: 'Australian Property Lawyers Strata', abnHint: null },
    'jpstrata.com.au':                { canonicalName: 'JP Strata Management',          abnHint: null },
    'whittles.com.au':                { canonicalName: 'Whittles Body Corporate',       abnHint: null },
};

/**
 * Extract the email domain (lowercased, no `@`).
 * Returns null on missing/invalid input.
 */
export function domainOfEmail(email: any) {
    if (!email || typeof email !== 'string') return null;
    const m = email.match(/@([A-Za-z0-9.\-]+)/);
    return m ? m[1]!.toLowerCase() : null;
}

/**
 * Strip common subdomain prefixes that don't change the parent organisation
 * (e.g. `mail.picagroup.com.au` → `picagroup.com.au`). Best-effort heuristic;
 * if the resulting domain isn't in STRATA_MANAGER_DOMAINS, the caller should
 * fall back to the original.
 */
function normaliseDomain(domain: any) {
    if (!domain) return null;
    const stripped = String(domain).toLowerCase().replace(/^(mail|smtp|email|sydney|melbourne|brisbane|nsw|vic|qld|wa|sa|act|tas|nt)\./, '');
    return stripped || domain;
}

/**
 * Identify the canonical Requester from a contact's email address.
 *   identifyRequesterFromEmail('sydney.admin@picagroup.com.au')
 *   → { domain: 'picagroup.com.au', canonicalName: 'PICA Group', isKnown: true }
 *
 *   identifyRequesterFromEmail('hello@acme-strata.com.au')
 *   → { domain: 'acme-strata.com.au', canonicalName: 'acme-strata.com.au', isKnown: false }
 *
 *   identifyRequesterFromEmail(null)
 *   → null
 */
export function identifyRequesterFromEmail(email: any) {
    const raw = domainOfEmail(email);
    if (!raw) return null;
    const norm = normaliseDomain(raw);
    // Inline casts, not a hoisted `const table`: a new const is a real runtime
    // statement and the emit-identity gate rejects it (it did - that is how this
    // comment came to exist). A cast is erased.
    const known = (STRATA_MANAGER_DOMAINS as any)[norm] || (STRATA_MANAGER_DOMAINS as any)[raw];
    if (known) {
        return { domain: norm || raw, canonicalName: known.canonicalName, abnHint: known.abnHint, isKnown: true };
    }
    // Unknown domain — use the domain itself as the canonical name placeholder.
    // The user can rename via the CRM detail panel later.
    return { domain: norm || raw, canonicalName: norm || raw, isKnown: false };
}

/**
 * Build a re-organisation plan from an array of Requester records and their
 * primary contact lists. Groups Requesters by email domain, picks a keeper
 * per cluster (preferring proper-named records over site-shaped names), and
 * returns the per-cluster merge + rename plan that the UI previews and the
 * sweep applies.
 *
 * @param {Array<{id, name, contacts: Array<{email}>}>} requesterRecords
 * @returns {Array<{
 *     domain: string,
 *     canonicalName: string,
 *     isKnownDomain: boolean,
 *     keeper: { id, name, currentContacts },
 *     losers: Array<{ id, name, currentContacts }>,
 *     willRenameKeeper: boolean,
 * }>}
 */
export function proposedReorgPlan(requesterRecords: any) {
    if (!Array.isArray(requesterRecords)) return [];
    const isSiteShaped = (name: any) => {
        const t = String(name || '').toLowerCase();
        return /\bowners?\b/.test(t) || /\bstrata\s*plan\b/.test(t) || /\bsp\s*\d/.test(t);
    };
    const buckets = new Map(); // domain → array of { record, identity }
    for (const rec of requesterRecords) {
        // Use the first contact email that resolves to a known/unknown domain
        let identity = null;
        for (const c of (rec.contacts || [])) {
            identity = identifyRequesterFromEmail(c?.email);
            if (identity) break;
        }
        if (!identity) continue; // skip records with no contact email — manual ID needed
        if (!buckets.has(identity.domain)) buckets.set(identity.domain, { identity, records: [] });
        buckets.get(identity.domain).records.push(rec);
    }
    const plans = [];
    for (const [domain, { identity, records }] of buckets) {
        // Skip clusters with only one record AND a name that already matches
        // the canonical (no work needed)
        const { canonicalName, isKnown } = identity;
        // Pick keeper: prefer a record whose name is NOT site-shaped; tie-break
        // by preferring an exact canonical-name match; final tie: oldest id-string.
        const ranked = [...records].sort((a, b) => {
            const aShaped = isSiteShaped(a.name) ? 1 : 0;
            const bShaped = isSiteShaped(b.name) ? 1 : 0;
            if (aShaped !== bShaped) return aShaped - bShaped; // proper-named first
            const aMatch = (a.name || '').toLowerCase() === canonicalName.toLowerCase() ? 0 : 1;
            const bMatch = (b.name || '').toLowerCase() === canonicalName.toLowerCase() ? 0 : 1;
            if (aMatch !== bMatch) return aMatch - bMatch;
            return String(a.id).localeCompare(String(b.id));
        });
        const keeper = ranked[0];
        const losers = ranked.slice(1);
        const willRenameKeeper = (keeper.name || '').toLowerCase() !== canonicalName.toLowerCase();
        // Only produce a plan if there's actual work (merges OR rename)
        if (losers.length === 0 && !willRenameKeeper) continue;
        plans.push({
            domain,
            canonicalName,
            isKnownDomain: isKnown,
            keeper,
            losers,
            willRenameKeeper,
        });
    }
    return plans;
}
